import { z } from 'zod';
import type { OfferFormPayload, PartnerOfferData } from './api/partner-offers.api';

const optionalText = z.string().trim().optional();

/**
 * An empty value, or an http(s) URL with a real host (a dot, or localhost-free), matching
 * what the Backend's `IsUrl` accepts so a link the form allows is not refused server-side.
 */
function isHttpLink(value: string): boolean {
  if (value === '') return true;
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
}

/** Shown on both claim fields when neither is filled (FR-114). */
export const CLAIM_REQUIRED_MESSAGE = 'Add a claim link or an email.';

/**
 * Create/Edit Offer form (FR-29, MYN-104) - the canonical Special Offers/
 * Perks field set from data-fields.md (re-supplied directly by the
 * engineer): Perk Title, Perk Subtitle, Perk Description, Time Period
 * Available (beginning/end dates), Cover Photo, Claim Link and/or Email (FR-114,
 * which replaced the single Call to Action), Notes for MNR. This is NOT the Figma create-offer screen's field set (offerType,
 * discount value/percent, promo code, redemption link, max redemptions,
 * limit-redemptions toggle) - none of those appear in the canonical table,
 * which outranks Figma per this ticket's established precedent, and the
 * Backend schema no longer has columns for them.
 *
 * No Logo field: the offer's logo is pulled from the partner's own profile
 * (shown read-only on this form), never collected here.
 */
export const offerFormSchema = z
  .object({
    title: z.string().trim().min(1, 'Perk Title is required').max(100, 'Perk Title must be 100 characters or fewer'),
    subtitle: z.string().trim().max(100, 'Perk Subtitle must be 100 characters or fewer').optional(),
    coverPhotoFileKey: optionalText,
    description: z
      .string()
      .trim()
      .min(1, 'Perk Description is required')
      .max(300, 'Perk Description must be 300 characters or fewer'),
    availableFrom: optionalText,
    expiresAt: optionalText,
    claimLinkUrl: z
      .string()
      .trim()
      .max(500, 'Claim Link must be 500 characters or fewer')
      .refine(isHttpLink, 'Enter a valid http(s) link')
      .optional(),
    claimEmail: z
      .string()
      .trim()
      .max(254, 'Email must be 254 characters or fewer')
      .refine((value) => value === '' || z.string().email().safeParse(value).success, 'Enter a valid email address')
      .optional(),
    internalNotes: optionalText,
  })
  .refine((values) => !values.availableFrom || !values.expiresAt || values.availableFrom <= values.expiresAt, {
    message: 'End date must be on or after the start date.',
    path: ['expiresAt'],
  })
  // FR-114: a member needs a working way to claim the offer. Reported on both
  // fields so the partner sees where to fix it; the Backend enforces the same rule.
  .superRefine((values, ctx) => {
    if (values.claimLinkUrl?.trim() || values.claimEmail?.trim()) return;
    for (const path of ['claimLinkUrl', 'claimEmail'] as const) {
      ctx.addIssue({ code: 'custom', path: [path], message: CLAIM_REQUIRED_MESSAGE });
    }
  });

export type OfferFormValues = z.infer<typeof offerFormSchema>;

/** Maps a fetched offer onto the form's default values (edit mode). */
export function toFormValues(data: PartnerOfferData): OfferFormValues {
  return {
    title: data.title ?? '',
    subtitle: data.subtitle ?? '',
    // The offer's current cover photo shows via `data.coverPhotoUrl` (passed
    // separately to `CoverPhotoUploadField`); this field only ever holds a
    // *new* upload's S3 key, so it starts empty even in edit mode.
    coverPhotoFileKey: '',
    description: data.description ?? '',
    availableFrom: data.availableFrom ? data.availableFrom.slice(0, 10) : '',
    expiresAt: data.expiresAt ? data.expiresAt.slice(0, 10) : '',
    claimLinkUrl: data.claimLinkUrl ?? '',
    claimEmail: data.claimEmail ?? '',
    internalNotes: data.internalNotes ?? '',
  };
}

/** Maps form values to the API payload shape. */
export function toApiPayload(values: OfferFormValues): OfferFormPayload {
  const { coverPhotoFileKey, availableFrom, expiresAt, claimLinkUrl, claimEmail, ...rest } = values;
  return {
    ...rest,
    // `null`, not '': a blank field must CLEAR a stored value on the Backend (an empty
    // string would be read as "field omitted" and leave the old one in place).
    claimLinkUrl: claimLinkUrl?.trim() ? claimLinkUrl.trim() : null,
    claimEmail: claimEmail?.trim() ? claimEmail.trim() : null,
    // Blank means "no new upload this save" - omit rather than send '' and
    // overwrite an existing cover photo on the Backend.
    ...(coverPhotoFileKey ? { coverPhotoFileKey } : {}),
    ...(availableFrom ? { availableFrom: new Date(availableFrom).toISOString() } : {}),
    ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
  };
}
